import { computed } from 'vue'

import { tryUseNuxtApp, useRequestHeaders } from 'nuxt/app'

import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { getConfig } from '@common/core-layer/config'
import { resolvePortalSettingsHost } from '@common/core-layer/utils/health-check.utils'

import { LocationService } from '../services/location.service'
import { useLocationStore } from '../store/location.store'

import type { LocationData, PortalSettingsCache } from '../types/location'

export const useLocation = () => {
  const locationStore = useLocationStore()
  const locationService = new LocationService()
  const { isGokollab, isMeet } = useGlobal()

  const getPortalSettingsCache = (): PortalSettingsCache | undefined => {
    if (!process.server) return undefined

    return (
      tryUseNuxtApp() as
        | (ReturnType<typeof tryUseNuxtApp> & {
            $portalSettingsCache?: PortalSettingsCache
          })
        | null
    )?.$portalSettingsCache
  }

  // Meet has no SSR pass: the browser fetches this reply and picks its auth mode
  // (`authV2Enabled`) from it, so it must never be answered from the HTTP cache.
  const fetchPortalSettingsDirect = (domain: string): Promise<LocationData> =>
    isMeet ? locationService.fetchPortalSettings(domain, { cache: 'no-cache' }) : locationService.fetchPortalSettings(domain)

  // Make config reactive - get it fresh each time we need it
  const getDefaultGokollabLocationData = (): LocationData => {
    const config = getConfig() // Get fresh config on each call

    return {
      locationId: config.GOKOLLAB_LOCATION_ID,
      currencySymbol: '$',
      currencyCode: 'USD',
      subDomain: '',
      customDomain: '',
      languagePreferenceBuilder: 'en',
      branding: {
        portalImage:
          'https://storage.googleapis.com/revex-client-portal-production/3WbV2CNaKqejVtXwDGID/settings/c3f5d02e-87c0-4848-895d-2a7c65b4d35f',
        logo: 'https://storage.googleapis.com/revex-client-portal-production/3WbV2CNaKqejVtXwDGID/settings/6d727caf-22a7-40e3-8ef8-af46f3816e35',
        favicon: 'https://storage.googleapis.com/revex-gokollab-communities-preview/favicon-safari-gokollab.png',
        brandColor1: null,
        brandColor2: null,
        portalBannerText: 'Gokollab',
        portalDescription: 'This is the official Kollab ',
        portalBackgroundImage: null,
        portalBannerImage: null,
        portalIcon: null,
        copyRight: null,
        supportEmail: null,
      },
      chatWidgetSettings: {
        widgetId: '',
        permissions: {
          communities: true,
          membership: true,
          home: true,
          affiliate: true,
        },
      },
      locationPermissions: {
        appointmentsEnabled: false,
        communitiesEnabled: true,
        membershipEnabled: true,
        eventsEnabled: true,
        affiliateManagerEnabled: true,
        loyaltyEnabled: false,
        subscriptionsEnabled: true,
        contractsEnabled: false,
        estimatesEnabled: true,
        sharedFilesEnabled: true,
        certificatesEnabled: false,
        discoverEnabled: true,
      },
      portalBannerText: 'Gokollab',
      portalDescription: 'This is the official Kollab ',
    }
  }

  // Methods
  const fetchLocation = async (domain?: string): Promise<LocationData | null> => {
    try {
      locationStore.setLoading(true)
      locationStore.setError(null)

      let locationData: LocationData

      // Use default GoKollab location data if isGokollab is true
      // This bypasses API calls and uses hardcoded location data for GoKollab
      if (isGokollab) {
        locationData = getDefaultGokollabLocationData()
      } else {
        // Use provided domain or get from service
        let targetDomain = domain

        if (!targetDomain) {
          // Get host from request headers if on server
          if (process.server) {
            try {
              const headers = useRequestHeaders(['host', 'domain'])
              const rawDomain = headers['domain']
              let domainHeader: string | undefined
              if (typeof rawDomain === 'string') {
                domainHeader = rawDomain
              } else if (Array.isArray(rawDomain) && typeof rawDomain[0] === 'string') {
                domainHeader = rawDomain[0]
              }
              const host = resolvePortalSettingsHost(headers.host || '', domainHeader)
              targetDomain = locationService.getDomainForSettings(host)
            } catch (error) {
              // Fallback if Nuxt context not available during SSR (e.g. prerender,
              // hook-timing failure). Defer to client-side init-location plugin
              // instead of poisoning the store with GoKollab data.
              console.warn('[useLocation] useRequestHeaders failed during SSR, deferring to client:', error)
              targetDomain = undefined
            }
          } else {
            targetDomain = locationService.getDomainForSettings()
          }
        }

        // No resolvable domain — branch by execution context to avoid SSR-polluting
        // the store with GoKollab data on non-GoKollab apps (the source of the
        // "auth.vue paints with wrong gradient on first paint" bug).
        if (!targetDomain) {
          if (process.server) {
            // SSR cannot resolve host (health check, prerender, hook-timing failure).
            // Leave the store empty so the client-side init-location plugin re-fetches
            // with window.location.hostname.
            //
            // Important: most store `locationPermissions` defaults are `true`
            // (`loyaltyEnabled` is closed). Leaving the rest untouched would let
            // `auth.global.ts → canAccessApp(...)` grant access to gated apps
            // (e.g. `/contracts`) on the SSR pass even though the location's real
            // permissions are unknown. Lock them to deny-by-default here; the
            // client tick's successful fetch overwrites them via
            // `setLocationData → setPermissions`.
            locationStore.setPermissions({
              appointmentsEnabled: false,
              membershipEnabled: false,
              eventsEnabled: false,
              affiliateManagerEnabled: false,
              loyaltyEnabled: false,
              communitiesEnabled: false,
              subscriptionsEnabled: false,
              contractsEnabled: false,
              estimatesEnabled: false,
              sharedFilesEnabled: false,
              certificatesEnabled: false,
              discoverEnabled: false,
            })
            console.debug('[useLocation] No resolvable domain on server; deferring to client-side fetch')
            return null
          }

          // Client-side last resort with no resolvable domain (e.g. file://):
          // fall back to GoKollab data to avoid a permanently empty store.
          console.debug('[useLocation] Internal/health check request detected on client, using default GoKollab data')
          locationData = getDefaultGokollabLocationData()
        } else {
          // When isGokollab is false, throw error if API call fails (don't fallback to GoKollab data)
          const portalSettingsCache = getPortalSettingsCache()
          locationData = portalSettingsCache
            ? await portalSettingsCache.get(targetDomain, normalizedDomain => locationService.fetchPortalSettings(normalizedDomain))
            : await fetchPortalSettingsDirect(targetDomain)
        }
      }

      // Store the data
      locationStore.setLocationData(locationData)

      // Apply branding on client side
      if (process.client) {
        locationStore.applyBranding()
      }

      return locationData
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch location data'
      locationStore.setError(errorMessage)
      throw err
    } finally {
      locationStore.setLoading(false)
    }
  }

  const setLocationData = (data: LocationData) => {
    locationStore.setLocationData(data)
  }

  const clearError = () => {
    locationStore.setError(null)
  }

  const reset = () => {
    locationStore.reset()
  }

  // Helper to check if a feature is enabled
  const isFeatureEnabled = (feature: keyof typeof locationStore.locationPermissions): boolean => {
    return locationStore.locationPermissions[feature] ?? false
  }

  // Initialize location on first use
  const initLocation = async () => {
    if (!locationStore.hasLocation && !locationStore.isLoading) {
      await fetchLocation()
    }
  }

  return {
    // State
    store: locationStore,
    locationId: computed(() => locationStore.locationId),

    // Methods
    fetchLocation,
    setLocationData,
    clearError,
    reset,
    isFeatureEnabled,
    initLocation,
    getDefaultGokollabLocationData,
  }
}
